import { useEffect, useMemo, useState } from 'react'
import IncidentsAPI from 'services/IncidentsAPI'
import CloseIncidentDialog from './CloseIncidentDialog'
import IncidentInvestigationPanel from './IncidentInvestigationPanel'
import IncidentContractorsSection from './IncidentContractorsSection'
import IncidentReportsSection from './IncidentReportsSection'
import SeverityQuickEdit from 'components/SeverityQuickEdit'
import SearchableSelect from 'components/SearchableSelect'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import EventIcon from '@mui/icons-material/Event'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import {
  getIncidentRoutine,
  getIncidentsRoutine,
  getIncidentStatsRoutine,
  selectCurrentIncident,
  selectCurrentIncidentLoading,
  selectIncidentMutationLoading,
} from 'features/incidents/ducks'
import {
  incidentCategoryLabel,
  SEVERITY_META,
  STATUS_META,
  ACTION_STATUS_META,
} from 'features/incidents/constants'

const formatDateTime = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

const IncidentDetailDrawer = ({ incidentId, open, onClose, onChanged }) => {
  const dispatch = useDispatch()
  const incident = useSelector(selectCurrentIncident)
  const loading = useSelector(selectCurrentIncidentLoading)
  const mutating = useSelector(selectIncidentMutationLoading)

  const [closeOpen, setCloseOpen] = useState(false)
  const openActionsCount = useMemo(() => {
    return (incident?.actions || []).filter((a) => a.status === 'open').length
  }, [incident])

  const reloadList = () => {
    if (onChanged) {
      onChanged()
      return
    }
    dispatch(getIncidentsRoutine({ page: 1, per_page: 25 }))
    dispatch(getIncidentStatsRoutine({}))
  }

  const performClose = async () => {
    if (!incident?.id) return
    try {
      await IncidentsAPI.close(incident.id)
      dispatch(getIncidentRoutine(incident.id))
      reloadList()
      toast.info('Incident closed.')
      setCloseOpen(false)
    } catch (e) {
      toast.error('Could not close this incident. Please try again.')
    }
  }

  const refresh = () => {
    if (!incident?.id) return
    dispatch(getIncidentRoutine(incident.id))
    reloadList()
  }

  const patchField = async (payload, msg) => {
    if (!incident?.id) return
    try {
      await IncidentsAPI.update(incident.id, payload)
      toast.success(msg)
      refresh()
    } catch (e) {
      toast.error('Could not save the change.')
    }
  }

  useEffect(() => {
    if (open && incidentId) {
      dispatch(getIncidentRoutine(incidentId))
    }
  }, [dispatch, open, incidentId])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  const isLoaded = incident && incident.id === incidentId
  const sev = isLoaded ? SEVERITY_META[incident.severity] : null
  const stat = isLoaded ? STATUS_META[incident.status] : null

  return (
    <>
      <Backdrop $open={open} onClick={onClose} />
      <Drawer $open={open} role="dialog" aria-label="Incident detail">
        <DrawerHeader>
          <PipRow>
            {sev && <Pip $colour={sev.colour} $bg={sev.bg}>{sev.label}</Pip>}
            {stat && <Pip $colour={stat.colour} $bg={stat.bg}>{stat.label}</Pip>}
          </PipRow>
          <HeaderActions>
            {isLoaded && (
              <FullPageLink
                href={`/incidents/${incident.id}`}
                title="Open full page"
                onClick={(e) => { e.preventDefault(); window.location.assign(`/incidents/${incident.id}`) }}
              >
                <OpenInNewIcon style={{ fontSize: 15 }} /> Full page
              </FullPageLink>
            )}
            <CloseBtn type="button" onClick={onClose} aria-label="Close drawer">
              <CloseIcon />
            </CloseBtn>
          </HeaderActions>
        </DrawerHeader>

        {!isLoaded || loading ? (
          <LoadingBlock>Loading incident…</LoadingBlock>
        ) : (
          <>
            <DrawerTitle>{incidentCategoryLabel(incident.category)}</DrawerTitle>

            <MetaRowInline>
              {incident.occurredAt && (
                <MetaInline>
                  <EventIcon style={{ fontSize: 14 }} />
                  Occurred {formatDateTime(incident.occurredAt)}
                </MetaInline>
              )}
              {incident.buildingPassport?.name && (
                <MetaInline>
                  <ApartmentOutlinedIcon style={{ fontSize: 14 }} />
                  {incident.buildingPassport.name}
                </MetaInline>
              )}
            </MetaRowInline>

            <Section>
              <Label>Classification</Label>
              <ClassRow>
                <ClassCol>
                  <MiniLabel>Severity</MiniLabel>
                  <SeverityQuickEdit
                    value={incident.severity}
                    disabled={!incident.permissions?.canUpdate}
                    onChange={(next) => patchField({ severity: next }, 'Severity updated.')}
                  />
                </ClassCol>
                <ClassCol>
                  <MiniLabel>Type</MiniLabel>
                  <SearchableSelect
                    value={incident.incidentType || 'incident'}
                    onChange={(v) => patchField({ incident_type: v }, 'Type updated.')}
                    options={[
                      { value: 'accident', label: 'Accident' },
                      { value: 'incident', label: 'Incident' },
                      { value: 'near_miss', label: 'Near miss' },
                    ]}
                    disabled={!incident.permissions?.canUpdate}
                    ariaLabel="Type"
                  />
                </ClassCol>
              </ClassRow>
            </Section>

            <Section>
              <Label>Narrative</Label>
              <Body>{incident.narrative}</Body>
            </Section>

            <Section>
              <IncidentReportsSection incident={incident} onUpdated={refresh} />
            </Section>

            <Section>
              <IncidentInvestigationPanel incident={incident} onUpdated={refresh} />
            </Section>

            {incident.attachments?.length > 0 && (
              <Section>
                <Label>Attachments ({incident.attachments.length})</Label>
                <AttachmentList>
                  {incident.attachments.map(att => (
                    <AttachmentItem key={att.id}>
                      {att.originalFilename || att.filename || `Attachment ${att.id}`}
                    </AttachmentItem>
                  ))}
                </AttachmentList>
              </Section>
            )}

            {incident.actions?.length > 0 && (
              <Section>
                <SectionHeader>
                  <Label>Generated remedials ({incident.actions.length})</Label>
                  <OpenAllLink
                    href={`/actions?incident=${incident.id}&status=open`}
                    onClick={(e) => {
                      // Use full navigation (no SPA push) so the actions list re-mounts
                      // and picks up the new ?incident= filter cleanly.
                      e.preventDefault()
                      window.location.assign(`/actions?incident=${incident.id}&status=open`)
                    }}
                  >
                    Open in remedials →
                  </OpenAllLink>
                </SectionHeader>
                <ActionList>
                  {incident.actions.map(a => {
                    const sev = SEVERITY_META[a.severity] || SEVERITY_META.low
                    const stat = ACTION_STATUS_META[a.status]
                    return (
                      <ActionItem key={a.id}>
                        <ActionSeverity $colour={sev.colour} $bg={sev.bg}>
                          {sev.label}
                        </ActionSeverity>
                        <ActionMain>
                          <ActionTitle>{a.title}</ActionTitle>
                          {a.suggestedDueBy && (
                            <ActionDue>Due {a.suggestedDueBy}</ActionDue>
                          )}
                        </ActionMain>
                        {stat && (
                          <ActionStatus $colour={stat.colour} $bg={stat.bg}>
                            {stat.label}
                          </ActionStatus>
                        )}
                      </ActionItem>
                    )
                  })}
                </ActionList>
              </Section>
            )}

            <IncidentContractorsSection incident={incident} onUpdated={refresh} />

            {incident.permissions?.canClose && incident.status !== 'closed' && (
              <ActionBar>
                <CloseIncidentButton
                  type="button"
                  onClick={() => setCloseOpen(true)}
                  disabled={mutating}
                >
                  Close incident
                </CloseIncidentButton>
              </ActionBar>
            )}
          </>
        )}
      </Drawer>

      <CloseIncidentDialog
        open={closeOpen}
        incidentTitle={(incident?.narrative || '').slice(0, 60)}
        openActionsCount={openActionsCount}
        onCancel={() => setCloseOpen(false)}
        onConfirm={performClose}
      />
    </>
  )
}

export default IncidentDetailDrawer

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`
const slideIn = keyframes`from { transform: translateX(100%); } to { transform: translateX(0); }`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 1100;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
  animation: ${({ $open }) => ($open ? fadeIn : 'none')} 0.2s ease;
`

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  box-sizing: border-box;
  width: min(520px, 92vw);
  background: white;
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.12);
  z-index: 1101;
  padding: 22px 26px 32px;
  overflow-y: auto;
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
  animation: ${({ $open }) => ($open ? slideIn : 'none')} 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`

const PipRow = styled.div`
  display: inline-flex;
  gap: 6px;
  flex-wrap: wrap;
`

const Pip = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${({ $bg }) => $bg || '#e2e8f0'};
  color: ${({ $colour }) => $colour || '#475569'};
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  padding: 4px;
  border-radius: 6px;
  display: inline-flex;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const FullPageLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 6px;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const ClassRow = styled.div`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  align-items: flex-end;
`

const ClassCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const MiniLabel = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 800;
  color: #6b7280;
`

const DrawerTitle = styled.h2`
  font-size: 19px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px;
  line-height: 1.35;
`

const MetaRowInline = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
`

const MetaInline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
`

const LoadingBlock = styled.div`
  padding: 32px 0;
  font-size: 14px;
  color: #64748b;
  text-align: center;
`

const Section = styled.div`
  margin: 18px 0;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const Label = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 8px;
`

const Body = styled.div`
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.haiti};
`

const AttachmentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const AttachmentItem = styled.li`
  padding: 6px 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  background: #f8fafc;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  margin-bottom: 6px;
  &:last-child { margin-bottom: 0; }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`

const OpenAllLink = styled.a`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const ActionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const ActionItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: white;
  margin-bottom: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  &:last-child { margin-bottom: 0; }
`

const ActionSeverity = styled.span`
  display: inline-flex;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg || '#e2e8f0'};
  color: ${({ $colour }) => $colour || '#475569'};
  flex-shrink: 0;
`

const ActionMain = styled.div`
  flex: 1;
  min-width: 0;
`

const ActionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.3;
`

const ActionDue = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`

const ActionStatus = styled.span`
  display: inline-flex;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg || '#e2e8f0'};
  color: ${({ $colour }) => $colour || '#475569'};
  flex-shrink: 0;
`

const ActionBar = styled.div`
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const CloseIncidentButton = styled.button`
  padding: 10px 18px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
